import React from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Box, LinearProgress, Backdrop, CircularProgress,
  Stack, Alert, Button, Typography, Divider, Chip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export default function CrearAsignacionForm({
  open,
  onClose,
  vendedores = [],
  clientes = [],
  selectedVendedor,
  selectedClientes = [],
  setSelectedVendedor,
  setSelectedClientes,
  loading = false, 
  loadingLists = false,
  error,
  onSubmit,
  onLoadMore,
  hasMore,
  loadingMore,
  totalCount = 0 // 👈 Recibimos el total
}) {
  const disabled = Boolean(loading);
  
  // Verificar si el vendedor seleccionado está activo
  const selectedVendedorData = vendedores.find(v => String(v.id) === String(selectedVendedor));
  const isSelectedVendedorActivo = selectedVendedorData ? selectedVendedorData.activo : false;
  
  // Solo permitir asignar si hay vendedor activo seleccionado y clientes seleccionados
  const canSubmit = !disabled && !loadingLists && selectedVendedor && isSelectedVendedorActivo && Array.isArray(selectedClientes) && selectedClientes.length > 0;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={false} 
      fullWidth={false}
      PaperProps={{ 
        sx: { width: '80vw', height: '80vh', maxWidth: '900px' } 
      }}
    >
      <DialogTitle sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            Crear asignación
          </Box>
          <IconButton aria-label="close" onClick={onClose} size="small" disabled={disabled}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {(loading || loadingLists) && <LinearProgress />}

      <DialogContent 
        sx={{ 
          position: "relative",
          height: '100%',
          p: 0,
        }}
      >
        <Backdrop
          open={loading}
          sx={{ position: "absolute", zIndex: (t) => t.zIndex.drawer + 1, color: "#fff" }}
          invisible={!loading}
        >
          {loading && <CircularProgress />}
        </Backdrop>

        <Stack spacing={2} sx={{ mb: 2, px: 2, pt: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>

        <Box
          component="form"
          onSubmit={onSubmit}
          sx={{ 
            display: "grid", 
            gridTemplateColumns: "1fr auto 1fr", 
            gap: 2, 
            px: 2,
            pb: 2,
            height: '90%',
            overflow: 'hidden'
          }}
        >
          {/* COLUMNA IZQUIERDA: Clientes */}
          <Box sx={{ display: 'flex', flexDirection: 'column', overflow: "hidden", border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2" fontWeight="600">
                Clientes sin asignación ({totalCount}) 
              </Typography>
              {clientes.length > 0 && (
                <Button
                  size="small"
                  variant="text"
                  disabled={disabled || loadingLists}
                  onClick={() => {
                    const allSelected = Array.isArray(selectedClientes) && selectedClientes.length >= clientes.length;
                    if (allSelected) setSelectedClientes([]);
                    else setSelectedClientes(clientes.map((c) => c.id));
                  }}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  {Array.isArray(selectedClientes) && selectedClientes.length >= clientes.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
                </Button>
              )}
            </Box>
            
            <Box sx={{ flex: 1, overflow: "auto", p: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {clientes.map((c) => {
                  const isSelected = Array.isArray(selectedClientes) && selectedClientes.includes(c.id);
                  return (
                    <Box
                      key={c.id}
                      onClick={() => {
                        if (disabled || loadingLists) return;
                        if (isSelected) setSelectedClientes((prev) => (prev || []).filter((id) => id !== c.id));
                        else setSelectedClientes([...(selectedClientes || []), c.id]);
                      }}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'white',
                        cursor: disabled || loadingLists ? 'default' : 'pointer',
                        opacity: disabled || loadingLists ? 0.6 : 1,
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          bgcolor: disabled || loadingLists ? undefined : (isSelected ? 'rgba(25, 118, 210, 0.12)' : '#f5f5f5'),
                        }
                      }}
                    >
                      <Typography fontWeight={isSelected ? 600 : 400} fontSize="0.9rem">
                        {c.nombre}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                        {c.consulta ?? "-"}
                      </Typography>
                    </Box>
                  );
                })}
                
                {hasMore && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <Button 
                      variant="text" 
                      size="small" 
                      onClick={onLoadMore} 
                      disabled={loadingMore || disabled}
                      startIcon={loadingMore ? <CircularProgress size={16} /> : <ExpandMoreIcon />}
                    >
                      {loadingMore ? "Cargando..." : "Ver más clientes"}
                    </Button>
                  </Box>
                )}
                
                {!hasMore && clientes.length > 0 && (
                  <Typography align="center" sx={{ py: 1, color: 'text.secondary', fontSize: '0.8rem' }}>
                    Fin de la lista
                  </Typography>
                )}

                {clientes.length === 0 && !loadingLists && (
                  <Typography align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No hay clientes sin asignación
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* COLUMNA DERECHA: Vendedores */}
          <Box sx={{ display: 'flex', flexDirection: 'column', overflow: "hidden", border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" fontWeight="600">
                Seleccionar Vendedor
              </Typography>
            </Box>
            <Box sx={{ flex: 1, overflow: "auto", p: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {vendedores.map((v) => {
                  const isSelected = String(selectedVendedor) === String(v.id);
                  const isInactive = !v.activo;
                  const hasClientsSelected = Array.isArray(selectedClientes) && selectedClientes.length > 0;
                  const shouldDisable = disabled || loadingLists || (isInactive && hasClientsSelected);
                  
                  return (
                    <Box
                      key={v.id}
                      onClick={() => {
                        if (shouldDisable) return;
                        setSelectedVendedor(v.id);
                      }}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'white',
                        cursor: shouldDisable ? 'default' : 'pointer',
                        opacity: shouldDisable ? 0.5 : 1,
                        transition: 'all 0.15s ease',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        '&:hover': {
                          bgcolor: shouldDisable ? undefined : (isSelected ? 'rgba(25, 118, 210, 0.12)' : '#f5f5f5'),
                        }
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap fontWeight={isSelected ? 600 : 400} fontSize="0.9rem">
                          {v.nombre}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap fontSize="0.8rem">
                          {v.email || v.telefono || ''}
                        </Typography>
                      </Box>

                      <Chip
                        label={v.activo ? 'ACTIVO' : 'INACTIVO'}
                        size="small"
                        sx={{
                          bgcolor: v.activo ? 'success.light' : 'error.light',
                          color: v.activo ? 'success.contrastText' : 'error.contrastText',
                          fontWeight: 600,
                          ml: 1,
                          flexShrink: 0
                        }}
                      />
                    </Box>
                  );
                })}

                {vendedores.length === 0 && !loadingLists && (
                  <Typography align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No hay vendedores disponibles
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ pr: 3, pb: 2, display: 'flex', gap: 2 }}>
        <Button onClick={onClose} disabled={disabled} color="inherit">Cancelar</Button>

        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={!canSubmit}
        >
          {loading ? "Creando..." : `Asignar (${selectedClientes.length})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
